import CopyIcon from '@mui/icons-material/ContentCopy';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import Stack from '@mui/material/Stack';
import type { AnswerWidgets } from '@openx/components-core/lib/AiChat/answerWidgets';
import { ChatActionButton } from '@openx/components-core/lib/AiChat/components/ChatActionButton';
import { getMessageId } from '@openx/components-core/lib/AiChat/messageId';
import { type Message, MessageRole } from '@openx/components-core/lib/AiChat/types';
import { answerWidgetClipboardComponents } from '@openx/components-core/lib/Markdown/widgets/answerWidgetClipboard';
import { copyRichTextToClipboard } from '@openx/utils/lib/copyToClipboard';
import { useTranslation } from 'react-i18next';
import type { Components } from 'react-markdown';
import type { MessageActionState } from '../constants';
import { DealTroubleshootingFeedbackForm } from './DealTroubleshootingFeedbackForm';

export type TroubleshootingMessage = Message & { langfuse_trace_id?: string };

type DealTroubleshootingMessageActionsProps = {
  message: TroubleshootingMessage;
  widgets?: AnswerWidgets;
  msgState: MessageActionState | undefined;
  isSubmitting: boolean;
  onThumbUp: (traceId: string, messageId: string) => void;
  onThumbDown: (messageId: string) => void;
  onSubmitFeedback: (messageId: string, traceId: string, feedbackText: string) => Promise<void>;
  onCancelFeedback: (messageId: string) => void;
};

const blockElements = new Set(['blockquote', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p']);

export const htmlToPlainText = (html: string) => {
  const container = document.createElement('div');
  container.innerHTML = html;

  const readNode = (node: Node): string => {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent ?? '';
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
      return '';
    }

    const element = node as HTMLElement;
    const tagName = element.tagName.toLowerCase();
    const childText = Array.from(element.childNodes).map(readNode).join('');

    if (tagName === 'br') {
      return '\n';
    }

    if (tagName === 'ol' || tagName === 'ul') {
      return Array.from(element.children)
        .map((child, index) => {
          const marker = tagName === 'ol' ? `${index + 1}. ` : '- ';
          return `${marker}${Array.from(child.childNodes).map(readNode).join('').trim()}\n`;
        })
        .join('');
    }

    if (tagName === 'td' || tagName === 'th') {
      return `${childText.trim()}\t`;
    }

    if (tagName === 'tr' || tagName === 'li') {
      return `${childText.trim()}\n`;
    }

    if (tagName === 'pre') {
      return `${childText.replace(/\n+$/g, '')}\n\n`;
    }

    if (blockElements.has(tagName)) {
      return `${childText.trim()}\n\n`;
    }

    return childText;
  };

  return Array.from(container.childNodes)
    .map(readNode)
    .join('')
    .replace(/\t+\n/g, '\n')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/^\n+|\n+$/g, '');
};

export const markdownToHtml = async (markdown: string, components: Components = {}) => {
  const [{ renderToStaticMarkup }, { default: ReactMarkdown }, { default: remarkGfm }] = await Promise.all([
    import('react-dom/server'),
    import('react-markdown'),
    import('remark-gfm'),
  ]);

  return renderToStaticMarkup(
    <ReactMarkdown components={components} remarkPlugins={[remarkGfm]}>
      {markdown}
    </ReactMarkdown>
  );
};

export const getTroubleshootingClipboardContent = async (
  message: TroubleshootingMessage,
  components: Components = {}
) => {
  if (message.role === MessageRole.EVENT || message.role === MessageRole.WIDGET) {
    return { plainText: '', htmlText: '' };
  }

  const markdown = [message.summary?.trim(), message.text.trim()].filter(Boolean).join('\n\n');
  const htmlText = await markdownToHtml(markdown, components);

  return {
    plainText: htmlToPlainText(htmlText),
    htmlText,
  };
};

const copyTroubleshootingMessage = async (
  message: TroubleshootingMessage,
  widgets: AnswerWidgets | undefined,
  notices: { errorNotice: string; emptyNotice: string }
) => {
  const clipboardContent = await getTroubleshootingClipboardContent(
    message,
    answerWidgetClipboardComponents(widgets, notices)
  );

  await copyRichTextToClipboard(clipboardContent);
};

export function DealTroubleshootingMessageActions({
  message,
  widgets,
  msgState,
  isSubmitting,
  onThumbUp,
  onThumbDown,
  onSubmitFeedback,
  onCancelFeedback,
}: DealTroubleshootingMessageActionsProps) {
  const { t } = useTranslation();
  const widgetNotices = { errorNotice: t("We couldn't display this data."), emptyNotice: t('No data.') };
  const traceId = message.langfuse_trace_id;
  const messageId = getMessageId(message);

  if (message.role === MessageRole.EVENT || message.role === MessageRole.WIDGET) {
    return null;
  }

  if (!traceId) {
    return (
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          gap: 1,
        }}
      >
        <ChatActionButton
          label={t('Copy to clipboard')}
          clickedLabel={t('Copied!')}
          onClick={() => copyTroubleshootingMessage(message, widgets, widgetNotices)}
          Icon={CopyIcon}
        />
      </Stack>
    );
  }

  return (
    <Stack
      direction="column"
      sx={{
        gap: 1,
      }}
    >
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          gap: 1,
        }}
      >
        {!msgState?.thumbDown && (
          <ChatActionButton
            label={t('Good answer')}
            clickedLabel={t('Feedback submitted')}
            onClick={() => onThumbUp(traceId, messageId)}
            Icon={msgState?.thumbUp ? ThumbUpIcon : ThumbUpOutlinedIcon}
          />
        )}
        {!msgState?.thumbUp && (
          <ChatActionButton
            label={t('Bad answer')}
            onClick={() => onThumbDown(messageId)}
            Icon={msgState?.thumbDown ? ThumbDownIcon : ThumbDownOutlinedIcon}
          />
        )}
        <ChatActionButton
          label={t('Copy to clipboard')}
          clickedLabel={t('Copied!')}
          onClick={() => copyTroubleshootingMessage(message, widgets, widgetNotices)}
          Icon={CopyIcon}
        />
      </Stack>

      {msgState?.requestFeedback && (
        <DealTroubleshootingFeedbackForm
          onSubmit={feedbackText => onSubmitFeedback(messageId, traceId, feedbackText)}
          onCancel={() => onCancelFeedback(messageId)}
          isSubmitting={isSubmitting}
        />
      )}
    </Stack>
  );
}
